import { Injectable } from '@angular/core';
import { Observable, forkJoin, map, of } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { AudienceDashboardService, FilterColumnMapping, PublicAudienceVolume } from './audience-engine.service';
import { AuthService } from './auth.service';

/**
 * Adapter service che trasforma le API Audience Dashboard
 * nel formato richiesto dai chart esistenti
 */
@Injectable({
  providedIn: 'root'
})
export class AudienceDashboardAdapterService {
  private cachedFilters: FilterColumnMapping[] | null = null;
  private daysBack = 30; // Default period

  constructor(
    private audienceDashboardService: AudienceDashboardService,
    private authService: AuthService
  ) {
    this.authService.selectedDcr$
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.cachedFilters = null;
      });
  }

  /**
   * Imposta il periodo di analisi in giorni
   */
  setDaysBack(days: number): void {
    this.daysBack = days;
  }

  /**
   * Carica i filtri una volta e li cachea
   */
  private getFilters(): Observable<FilterColumnMapping[]> {
    if (this.cachedFilters) {
      return of(this.cachedFilters);
    }

    return this.audienceDashboardService.getFilters().pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        this.cachedFilters = response.payload || [];
        return this.cachedFilters;
      })
    );
  }

  /**
   * Ottiene il totale utenti con trend
   * Formato compatibile con TotalUsersTrendResponse
   */
  getTotalUsersTrend(filterColumns: string[] = []): Observable<any> {
    const totals$ = this.audienceDashboardService.getChartTotals(this.daysBack, filterColumns);
    const daily$ = this.audienceDashboardService.getChartDailyTrend(this.daysBack, filterColumns, []);

    return forkJoin({
      totals: totals$,
      daily: daily$
    }).pipe(
      map(({ totals, daily }) => {
        if (totals.error || daily.error) {
          throw new Error(totals.error?.message || daily.error?.message);
        }

        const totalsRow = totals.payload?.[0] ?? {};
        const totalUsers = Number(totalsRow?.TNCIDs ?? 0);
        const dailyData = Array.isArray(daily.payload) ? daily.payload : [];

        // Calcola la percentuale di crescita (confronto con periodo precedente)
        let percentage = '+0.0%';
        let growthType: 'positive' | 'negative' = 'positive';

        if (dailyData.length >= 2) {
          const recent = dailyData.slice(-7).reduce((sum, d) => sum + d.TNCIDs, 0) / 7;
          const previous = dailyData.slice(0, 7).reduce((sum, d) => sum + d.TNCIDs, 0) / 7;

          if (previous > 0) {
            const growth = ((recent - previous) / previous) * 100;
            growthType = growth >= 0 ? 'positive' : 'negative';
            percentage = `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;
          }
        }

        return {
          value: this.formatNumber(totalUsers),
          percentage,
          growthType,
          chartData: dailyData.map((d: any) => ({
            date: this.formatDate(d.Date),
            users: d.TNCIDs
          }))
        };
      })
    );
  }

  /**
   * Ottiene gli utenti per categoria (CLUSTERS vs NULL)
   * CLUSTERS = utenti con enriched (ObjectType='enriched', ObjectName='Cluster')
   * NULL = utenti senza enriched
   * Formato compatibile con UsersCategoryResponse[]
   */
  getUsersCategory(filterColumns: string[] = []): Observable<any[]> {
    const totals$ = this.audienceDashboardService.getChartTotals(this.daysBack, filterColumns);

    return forkJoin({
      totals: totals$,
      filters: this.getFilters()
    }).pipe(
      map(({ totals, filters }) => {
        if (totals.error) {
          throw new Error(totals.error.message);
        }

        const data = totals.payload?.[0] ?? {};
        const totalUsers = Number(data?.TNCIDs ?? 0);

        // Trova la colonna enriched con ObjectName='Clusters' (nota: con la S finale)
        const enrichedColumn = filters.find(f =>
          f.ObjectType.toLowerCase() === 'enriched' &&
          f.ObjectName.toLowerCase() === 'clusters'
        );


        let clusterUsers = 0;
        if (enrichedColumn) {
          // Usa la colonna enriched per contare gli utenti in cluster
          clusterUsers = Number(data[enrichedColumn.ColumnName] ?? 0);
        }

        // NULL = totale - cluster
        const nullUsers = totalUsers - clusterUsers;

        // Calcola percentuali
        const clusterPerc = totalUsers > 0 ? Math.round((clusterUsers / totalUsers) * 100) : 0;
        const nullPerc = totalUsers > 0 ? Math.round((nullUsers / totalUsers) * 100) : 0;

        return [
          {
            category: 'CLUSTERS',
            percentage: clusterPerc,
            value: `${this.formatNumber(clusterUsers)} | ${clusterPerc}%`
          },
          {
            category: 'NULL',
            percentage: nullPerc,
            value: `${this.formatNumber(nullUsers)} | ${nullPerc}%`
          }
        ];
      })
    );
  }

  /**
   * Ottiene gli utenti per device
   * Formato compatibile con UsersDeviceResponse[]
   */
  getUsersDevice(filterColumns: string[] = []): Observable<any[]> {
    const totals$ = this.audienceDashboardService.getChartTotals(this.daysBack, filterColumns);

    return forkJoin({
      totals: totals$,
      filters: this.getFilters()
    }).pipe(
      map(({ totals, filters }) => {
        if (totals.error) {
          throw new Error(totals.error.message);
        }

        const data = totals.payload?.[0] ?? {};
        const totalUsers = Number(data?.TNCIDs ?? 0);

        // Trova le colonne device
        const deviceColumns = filters.filter(f => f.ObjectType === 'device');

        if (totalUsers <= 0) {
          return [];
        }

        return deviceColumns.map(deviceCol => {
          const deviceUsers = Number(data[deviceCol.ColumnName] ?? 0);
          const percentage = Math.round((deviceUsers / totalUsers) * 100);

          return {
            device: deviceCol.ObjectName,
            percentage,
            value: `${percentage}%`
          };
        }).filter(d => d.percentage > 0);
      })
    );
  }

  /**
   * Ottiene i dati audience per multi-line chart
   * Formato compatibile con AudiencePublicDataResponse[]
   */
  getAudiencePublicData(audienceColumns: string[] = []): Observable<any[]> {
    const daily$ = this.audienceDashboardService.getChartDailyTrend(
      this.daysBack,
      [],
      audienceColumns
    );

    return daily$.pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message);
        }

        const dailyData = response.payload || [];

        return dailyData.map((d: any) => ({
          month: this.formatDate(d.Date),
          values: audienceColumns.map(col => d[col] || 0)
        }));
      })
    );
  }

  /**
   * Ottiene i dati treemap per le audience
   * Formato compatibile con AudienceTreemapResponse[]
   */
  getAudienceTreemap(
    audienceIds: string[],
    audienceColors: { [key: string]: string },
    objectType: string = 'audience',
    filterColumns: string[] = []
  ): Observable<any[]> {
    const totals$ = this.audienceDashboardService.getChartTotals(this.daysBack, filterColumns);

    return forkJoin({
      totals: totals$,
      filters: this.getFilters()
    }).pipe(
      map(({ totals, filters }) => {
        if (totals.error) {
          throw new Error(totals.error.message);
        }

        const data = totals.payload?.[0] ?? {};
        const totalUsers = Number(data?.TNCIDs ?? 0);

        // Trova le colonne audience (usa objectType parametrizzato)
        const audienceColumns = filters.filter(f =>
          f.ObjectType === objectType && audienceIds.includes(f.ColumnName)
        );

        return audienceColumns.map(audCol => {
          const audUsers = Number(data[audCol.ColumnName] ?? 0);
          const percentage = totalUsers > 0 ? Math.round((audUsers / totalUsers) * 100) : 0;

          return {
            id: audCol.ColumnName,
            name: audCol.ObjectName,
            value: percentage,
            user_count: audUsers,
            color: audienceColors[audCol.ColumnName] || '#3b82f6',
            label: `${this.formatNumber(audUsers)} | ${percentage}%`
          };
        });
      })
    );
  }

  /**
   * Ottiene i volumi audience con quota di popolazione italiana
   */
  getPublicAudienceVolumes(audienceIds: string[] = []): Observable<Array<{
    id: string;
    name: string;
    volume: number;
    populationShare: number;
    updateDate?: string;
    type: string;
  }>> {
    return this.audienceDashboardService.getPublicAudienceVolumes().pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message);
        }

        const rows = (response.payload || []) as PublicAudienceVolume[];
        const allowedIds = new Set(audienceIds);

        return rows
          .filter(row => allowedIds.size === 0 || allowedIds.has(row.ColumnName))
          .map(row => {
            const rawShare = Number(row.PopulationShare) || 0;
            const populationShare = Number((rawShare * 100).toFixed(2));
            return {
              id: row.ColumnName,
              name: row.ObjectName,
              volume: Number(row.Volumes) || 0,
              populationShare,
              updateDate: row.UpdateDate ? new Date(row.UpdateDate).toISOString() : undefined,
              type: (row.ObjectType || '').toLowerCase()
            };
          })
          .sort((a, b) => b.populationShare - a.populationShare);
      })
    );
  }

  /**
   * Carica tutti i chart della dashboard in un'unica chiamata
   * @deprecated Non usare - logica difettosa con Observable nidificati. Usare le chiamate individuali.
   */
  getAllDashboardCharts(audienceIds: string[], audienceColors: { [key: string]: string }): Observable<any> {
    // NOTA: Questo metodo ha una logica difettosa e non funziona correttamente.
    // Utilizzare invece le chiamate individuali ai metodi getTotalUsersTrend, getUsersCategory, etc.
    throw new Error('getAllDashboardCharts è deprecato. Utilizzare le chiamate individuali.');
  }

  /**
   * Ottiene i dati della tabella con tutte le combinazioni
   * @param visibleColumns - Colonne visibili nella tabella
   */
  getTableData(visibleColumns?: any, filterColumns?: string[]): Observable<any[]> {
    const tableData$ = this.audienceDashboardService.getTableData(this.daysBack, visibleColumns, filterColumns);
    return tableData$;
  }

  /**
   * Utility: formatta numeri (es. 21789 -> "21.8K")
   */
  private formatNumber(num: number): string {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  }

  /**
   * Utility: formatta date (es. "2025-10-01T22:00:00.000Z" -> "1 ott")
   */
  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const day = date.getDate();
    const monthNames = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu',
                       'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
    const month = monthNames[date.getMonth()];
    return `${day} ${month}`;
  }
}
